import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card, Stack, Container, Typography, TextField,
    IconButton, InputAdornment, Alert, Box, Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function FounderLogin() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [tempToken, setTempToken] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [apiError, setApiError] = useState('');
    const [apiInfo, setApiInfo] = useState('');

    const Step1Schema = Yup.object().shape({
        email: Yup.string().email('Must be a valid email').required('Email is required'),
        password: Yup.string().required('Password is required'),
    });

    const step1Formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: Step1Schema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            setApiInfo('');
            try {
                const res = await syndicationApi.post('founder/login/', values);
                if (res.data?.temp_token) {
                    setTempToken(res.data.temp_token);
                    setStep(2);
                }
            } catch (err) {
                const data = err.response?.data;
                const code = data?.code;
                if (code === '2FA_REQUIRED') {
                    if (data?.setup_token) {
                        navigate(`/founder/setup-2fa?token=${data.setup_token}`);
                        return;
                    }
                    setApiError('Please complete your 2FA setup first. Check your email.');
                } else if (code === 'EMAIL_NOT_VERIFIED') {
                    setApiError(data?.error || 'Please set your password first.');
                } else if (code === 'SUSPENDED') {
                    setApiError(data?.error || 'Account suspended.');
                } else if (code === 'RATE_LIMITED') {
                    setApiError(data?.error || 'Too many attempts. Try later.');
                } else {
                    setApiError(data?.error || 'Login failed.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const Step2Schema = Yup.object().shape({
        totp_code: Yup.string().length(6, 'Must be 6 digits').required('TOTP code is required'),
    });

    const step2Formik = useFormik({
        initialValues: { totp_code: '' },
        validationSchema: Step2Schema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                const res = await syndicationApi.post('founder/login/2fa/', {
                    temp_token: tempToken,
                    totp_code: values.totp_code,
                });
                if (res.data?.token) {
                    localStorage.setItem('founderAuthToken', res.data.token);
                    localStorage.setItem('founderUser', JSON.stringify(res.data.user));
                    navigate('/founder/deals');
                }
            } catch (err) {
                const data = err.response?.data;
                if (data?.code === 'INVALID_TEMP_TOKEN') {
                    setApiError('Session expired. Please log in again.');
                    setStep(1);
                    setTempToken('');
                } else if (data?.code === 'TOTP_RATE_LIMITED') {
                    setApiError('Too many failed attempts. Please log in again.');
                    setStep(1);
                    setTempToken('');
                } else {
                    setApiError(data?.error || 'Invalid TOTP code.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <RootStyle title="Founder Login | Gateway X">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>Founder Login</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Gateway X Syndication — Founder Portal
                        </Typography>
                    </Box>

                    {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
                    {apiInfo && <Alert severity="info" sx={{ mb: 2 }}>{apiInfo}</Alert>}

                    {step === 1 ? (
                        <FormikProvider value={step1Formik}>
                            <Form autoComplete="off" noValidate onSubmit={step1Formik.handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth label="Email address" autoComplete="email"
                                        {...step1Formik.getFieldProps('email')}
                                        error={Boolean(step1Formik.touched.email && step1Formik.errors.email)}
                                        helperText={step1Formik.touched.email && step1Formik.errors.email}
                                    />
                                    <TextField
                                        fullWidth label="Password" type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        {...step1Formik.getFieldProps('password')}
                                        error={Boolean(step1Formik.touched.password && step1Formik.errors.password)}
                                        helperText={step1Formik.touched.password && step1Formik.errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <LoadingButton
                                        fullWidth size="large" type="submit" variant="contained"
                                        loading={step1Formik.isSubmitting}
                                        sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                                    >
                                        Continue
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    ) : (
                        <FormikProvider value={step2Formik}>
                            <Form autoComplete="off" noValidate onSubmit={step2Formik.handleSubmit}>
                                <Stack spacing={3}>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                        Enter the 6-digit code from your authenticator app.
                                    </Typography>
                                    <TextField
                                        fullWidth label="TOTP Code" autoFocus
                                        inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                                        {...step2Formik.getFieldProps('totp_code')}
                                        error={Boolean(step2Formik.touched.totp_code && step2Formik.errors.totp_code)}
                                        helperText={step2Formik.touched.totp_code && step2Formik.errors.totp_code}
                                    />
                                    <LoadingButton
                                        fullWidth size="large" type="submit" variant="contained"
                                        loading={step2Formik.isSubmitting}
                                        sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                                    >
                                        Verify & Login
                                    </LoadingButton>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Link component="button" variant="body2" onClick={() => { setStep(1); setTempToken(''); setApiError(''); }}>
                                            Back to login
                                        </Link>
                                    </Box>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    )}
                </FormCard>
            </Container>
        </RootStyle>
    );
}
